import { Injectable } from '@angular/core';
import { where } from '@angular/fire/firestore';
import {
  AccountingEntry,
  Club,
  Movement,
  MovementType,
  Optional,
} from '@models';
import { excludeEntriesLinkedToMovements } from '@utils';
import {
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  switchMap,
} from 'rxjs';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { TenantContextService } from './tenant-context.service';

/**
 * Accounting entries store `fiscalYear` inconsistently: sometimes as a single
 * year ("2026") and sometimes as a full season string ("2025-2026", when linked
 * from a movement). Match a season ("2025-2026") against both formats.
 */
function matchesSeason(fiscalYear: string | undefined, season: string): boolean {
  if (!fiscalYear) return false;
  return fiscalYear === season || fiscalYear === season.split('-')[1];
}

@Injectable({
  providedIn: 'root',
})
export class ClubsService {
  public all$: Observable<Club[]>;
  public clubBalance$: Observable<
    (Club & { ingresos: number; gastos: number })[]
  >;

  constructor(
    private firestore: FirestoreService,
    private tenantContext: TenantContextService,
    private seasonManager: SeasonManagerService,
  ) {
    this.all$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<Club>(
          this.tenantContext.collections.CLUBS,
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.clubBalance$ = combineLatest([
      this.all$,
      this.seasonManager.season$.pipe(
        switchMap((season) =>
          combineLatest([
            this.firestore.listDocuments<Movement>(
              this.tenantContext.collections.MOVEMENTS,
              where('seasonYear', '==', season),
            ),
            this.firestore.listDocuments<AccountingEntry>(
              this.tenantContext.collections.ACCOUNTING,
            ),
          ]).pipe(map(([movements, entries]) => ({ season, movements, entries }))),
        ),
      ),
    ]).pipe(
      map(([clubs, { season, movements, entries }]) =>
        clubs.map((club) => {
          const clubMovements = movements.filter((m) => m.clubId === club.id);
          // Entries registered from a movement (`movementId`) are already
          // counted via that movement — skip them to avoid double counting.
          const clubEntries = excludeEntriesLinkedToMovements(
            entries.filter(
              (e) =>
                e.clubId === club.id &&
                // Prefer the explicit season captured at creation time; fall back
                // to the fiscalYear heuristic for legacy entries without it.
                (e.seasonYear
                  ? e.seasonYear === season
                  : matchesSeason(e.fiscalYear, season)),
            ),
            clubMovements,
          );
          const ingresos =
            clubMovements
              .filter((m) => m.movementType === MovementType.INCOME)
              .reduce((sum, m) => sum + +m.amount, 0) +
            clubEntries
              .filter((e) => e.type === 'INCOME')
              .reduce((sum, e) => sum + +(e.totalAmount ?? e.amount), 0);
          const gastos =
            clubMovements
              .filter((m) => m.movementType === MovementType.EXPENDITURE)
              .reduce((sum, m) => sum + +m.amount, 0) +
            clubEntries
              .filter((e) => e.type === 'EXPENDITURE')
              .reduce((sum, e) => sum + +(e.totalAmount ?? e.amount), 0);
          return { ...club, ingresos, gastos };
        }),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private get col() {
    return this.tenantContext.collections.CLUBS;
  }

  get(id: string) {
    return this.firestore.getDocument<Club>(this.col, id);
  }

  create(club: Omit<Club, 'id'>) {
    return this.firestore.create<Club>(this.col, club);
  }

  update(id: string, club: Partial<Club>) {
    return this.firestore
      .update<Club>(this.col, { id, ...club })
      .then(() => id);
  }

  createOrUpdate(club: Optional<Club, 'id'>) {
    return club.id ? this.update(club.id, club) : this.create(club);
  }

  checkIfDuplicatedCif(cif: string) {
    return firstValueFrom(
      this.firestore.findDocument<Club>(this.col, where('cif', '==', cif)),
    );
  }
}
